import { useEffect, useId, useRef, useState, type RefObject } from "react";

export interface AcaoDoMenu {
  rotulo: string;
  testid: string;
  aoEscolher: () => void;
  perigosa?: boolean;
}

/**
 * O menu "Mais ações" do cabeçalho da implantação (SUS-15400). Reúne o que é raro e não deve
 * disputar espaço com o trabalho do dia, como excluir a implantação. Abre e fecha pelo botão,
 * fecha com Esc (o foco volta ao botão) e com clique fora. Sem ação disponível, o botão não
 * aparece: um menu vazio seria uma promessa sem nada atrás. `refDoBotao` deixa quem usa o menu
 * devolver o foco ao botão depois de uma ação que abre outra coisa e é desistida (WCAG 2.4.3).
 */
export function MaisAcoes({
  acoes,
  refDoBotao,
}: {
  acoes: readonly AcaoDoMenu[];
  refDoBotao?: RefObject<HTMLButtonElement | null>;
}) {
  const [aberto, setAberto] = useState(false);
  const proprio = useRef<HTMLButtonElement>(null);
  const botao = refDoBotao ?? proprio;
  const caixa = useRef<HTMLDivElement>(null);
  const idDoMenu = useId();

  useEffect(() => {
    if (!aberto) return;
    function fora(evento: MouseEvent) {
      if (!caixa.current?.contains(evento.target as Node)) setAberto(false);
    }
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, [aberto]);

  if (acoes.length === 0) return null;

  return (
    <div
      className="mais-acoes"
      ref={caixa}
      onKeyDown={(evento) => {
        if (evento.key === "Escape" && aberto) {
          setAberto(false);
          botao.current?.focus();
        }
      }}
    >
      <button
        ref={botao}
        type="button"
        className="botao botao--fantasma botao--pequeno"
        aria-expanded={aberto}
        aria-controls={idDoMenu}
        data-testid="mais-acoes"
        onClick={() => setAberto((valor) => !valor)}
      >
        Mais ações <span aria-hidden="true">▾</span>
      </button>
      {aberto ? (
        <ul className="mais-acoes__lista" id={idDoMenu}>
          {acoes.map((acao) => (
            <li key={acao.testid}>
              <button
                type="button"
                className={acao.perigosa ? "mais-acoes__item mais-acoes__item--perigosa" : "mais-acoes__item"}
                data-testid={acao.testid}
                onClick={() => {
                  setAberto(false);
                  acao.aoEscolher();
                }}
              >
                {acao.rotulo}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
